// Wave B · R2 (Vania 9 Oct 2026) · the crop box IS the preview.
//
// Draggable crop box over the admin-only raw screenshot. Mouse + touch.
// FREE SHAPE · portrait, landscape or square, no ratio presets, no ratio
// sizes. The separate preview panel is gone · the box on the screenshot
// shows the frame you are saving. Works for portrait posters (taller
// than wide) as well as landscape.
//
// Buttons under the screenshot:
//   · "save crop" with a small 's' key chip inside (Kili yellow, keyboard
//     "s" fires save, label never wraps · W-B.R8 item 7)
//   · "reset the box"
// Link beneath: "no poster in this screenshot" · marks the row's
// cover_source as skipped so staff can move past screenshots that
// have no event poster.
//
// Save hits POST /api/admin/drive-watcher/manual-crop/{pending_id}
// with a 0-1 normalised bbox; the backend stores a NEW cropped file
// and returns `cover_image_url`, `cover_source`, `cover_bbox`,
// `manual_crop_at` (W-C.45 · one trip, no reload).
//
// IMPORTANT: the raw screenshot never leaves the admin surface · this
// component loads it from /api/admin/drive-watcher/blob/{file_id}
// (admin-gated). The resulting manual crop is published; the raw
// screenshot is not.
import React, { useEffect, useRef, useState, useCallback } from "react";
import { client } from "@/lib/api";
import { toast } from "sonner";

const DEFAULT_BBOX = { left: 0.0, top: 0.08, right: 1.0, bottom: 0.86 };
const KILI = "#FFD60A";
const INK = "#1A1A30";

function clamp01(n) { return Math.max(0, Math.min(1, n)); }

export default React.forwardRef(function ManualCropEditor({
  pendingId,
  driveFileId,
  uploadUrl = "",
  fallbackUrl = "",
  initialBbox,
  onSaved,
  onCancel,
  onNoPoster,
  embedded = false,
}, ref) {
  const [bbox, setBbox] = useState(() => {
    const b = initialBbox && typeof initialBbox === "object" ? initialBbox : DEFAULT_BBOX;
    return {
      left: clamp01(Number(b.left ?? DEFAULT_BBOX.left)),
      top: clamp01(Number(b.top ?? DEFAULT_BBOX.top)),
      right: clamp01(Number(b.right ?? DEFAULT_BBOX.right)),
      bottom: clamp01(Number(b.bottom ?? DEFAULT_BBOX.bottom)),
    };
  });
  const [saving, setSaving] = useState(false);
  const canvasRef = useRef(null);
  const scrollRef = useRef(null);
  const imgRef = useRef(null);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgDims, setImgDims] = useState(null);
  // R19 FAIL A · fit must show the WHOLE screenshot inside the pane.
  // "fit" fits both W + H (object-contain); numeric zoom scrolls.
  const [zoom, setZoom] = useState("fit");
  const [fitSize, setFitSize] = useState({ w: 300, h: 400 });

  const rawUrl = uploadUrl
    ? `${process.env.REACT_APP_BACKEND_URL}${uploadUrl}`
    : (fallbackUrl || `${process.env.REACT_APP_BACKEND_URL}/api/admin/drive-watcher/blob/${driveFileId}`);

  const onImgLoad = useCallback((e) => {
    setImgLoaded(true);
    const el = e?.currentTarget || imgRef.current;
    if (el) setImgDims({ naturalWidth: el.naturalWidth, naturalHeight: el.naturalHeight });
  }, []);

  // R19 FAIL A · compute fit size that fits BOTH width and height.
  useEffect(() => {
    if (!scrollRef.current) return;
    const compute = () => {
      if (!scrollRef.current) return;
      const cw = Math.max(80, scrollRef.current.clientWidth - 8);
      const ch = Math.max(80, scrollRef.current.clientHeight - 8);
      const ar = (imgDims && imgDims.naturalWidth && imgDims.naturalHeight)
        ? imgDims.naturalWidth / imgDims.naturalHeight
        : 3 / 4;
      let w = cw; let h = w / ar;
      if (h > ch) { h = ch; w = ch * ar; }
      setFitSize({ w: Math.max(100, w), h: Math.max(100, h) });
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(scrollRef.current);
    window.addEventListener("resize", compute);
    return () => { ro.disconnect(); window.removeEventListener("resize", compute); };
  }, [imgDims]);

  const dragRef = useRef(null);
  const startDrag = (mode) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = canvasRef.current.getBoundingClientRect();
    const point = e.touches ? e.touches[0] : e;
    dragRef.current = {
      mode,
      startX: point.clientX,
      startY: point.clientY,
      rectW: rect.width,
      rectH: rect.height,
      startBbox: { ...bbox },
    };
  };

  const onMove = useCallback((e) => {
    if (!dragRef.current) return;
    e.preventDefault();
    const point = e.touches ? e.touches[0] : e;
    const dxN = (point.clientX - dragRef.current.startX) / dragRef.current.rectW;
    const dyN = (point.clientY - dragRef.current.startY) / dragRef.current.rectH;
    const start = dragRef.current.startBbox;
    const next = { ...start };
    const minSize = 0.04;    // free shape · portrait + landscape both allowed
    const mode = dragRef.current.mode;
    if (mode === "move") {
      const w = start.right - start.left;
      const h = start.bottom - start.top;
      let nl = clamp01(start.left + dxN);
      let nt = clamp01(start.top + dyN);
      if (nl + w > 1) nl = 1 - w;
      if (nt + h > 1) nt = 1 - h;
      next.left = nl; next.top = nt;
      next.right = nl + w; next.bottom = nt + h;
    } else {
      if (mode.includes("l")) next.left = clamp01(Math.min(start.left + dxN, start.right - minSize));
      if (mode.includes("r")) next.right = clamp01(Math.max(start.right + dxN, start.left + minSize));
      if (mode.includes("t")) next.top = clamp01(Math.min(start.top + dyN, start.bottom - minSize));
      if (mode.includes("b")) next.bottom = clamp01(Math.max(start.bottom + dyN, start.top + minSize));
    }
    setBbox(next);
  }, []);

  const endDrag = useCallback(() => { if (dragRef.current) dragRef.current = null; }, []);

  useEffect(() => {
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", endDrag);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", endDrag);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", endDrag);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", endDrag);
    };
  }, [onMove, endDrag]);

  const reset = useCallback(() => setBbox(DEFAULT_BBOX), []);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      const r = await client.post(`/admin/drive-watcher/manual-crop/${pendingId}`, uploadUrl
        ? { source: "upload", bbox }
        : { drive_file_id: driveFileId, bbox });
      toast.success("crop saved");
      onSaved?.(r.data);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "save failed";
      toast.error(`manual crop failed · ${typeof msg === "string" ? msg : JSON.stringify(msg)}`);
    } finally {
      setSaving(false);
    }
  }, [pendingId, driveFileId, uploadUrl, bbox, onSaved]);

  // W-B.R16 · PosterEditor top bar invokes save/getBbox through this ref.
  React.useImperativeHandle(ref, () => ({
    save,
    getBbox: () => bbox,
  }), [save, bbox]);

  // W-B.R2 · keyboard "s" saves the crop. Only fires when the editor is
  // mounted and the user is not typing in a field.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "s" && e.key !== "S") return;
      const t = e.target;
      const typing = t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable);
      if (typing) return;
      e.preventDefault();
      save();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [save]);

  const pct = (n) => `${(n * 100).toFixed(2)}%`;
  const boxStyle = {
    position: "absolute",
    left: pct(bbox.left), top: pct(bbox.top),
    width: pct(bbox.right - bbox.left), height: pct(bbox.bottom - bbox.top),
    boxShadow: "0 0 0 9999px rgba(0,0,0,0.55)",
    outline: `2px solid ${KILI}`,
    cursor: "move", touchAction: "none",
  };
  const handleBase = {
    position: "absolute", width: 18, height: 18, background: KILI,
    border: `2px solid ${INK}`, borderRadius: 4, touchAction: "none",
  };
  const h = (opts) => ({ ...handleBase, ...opts });

  return (
    <div data-testid="manual-crop-editor" className="flex min-h-0 h-full flex-col gap-2">
      {/* R19 · zoom toolbar · fit / 100 / 150 / 200 */}
      <div className="flex shrink-0 items-center gap-1 text-[11px]">
        <span style={{ color: "#8A8171" }}>zoom:</span>
        {[["fit", "fit"], [1, "100"], [1.5, "150"], [2, "200"]].map(([v, label]) => (
          <button key={label} type="button"
                  data-testid={`crop-zoom-${label}`}
                  onClick={() => setZoom(v)}
                  className="whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-bold uppercase"
                  style={zoom === v
                    ? { borderColor: INK, background: INK, color: "#FFFFFF" }
                    : { borderColor: "#8A8171", background: "#FFFFFF", color: "#5C5548" }}>
            {label === "fit" ? "fit whole" : `${label}%`}
          </button>
        ))}
      </div>
      <div ref={scrollRef}
           data-testid="manual-crop-scroll"
           className="relative flex min-h-0 flex-1 justify-center overflow-auto"
           style={{ touchAction: "pan-x pan-y", overscrollBehavior: "contain" }}>
        <div ref={canvasRef}
             data-testid="manual-crop-canvas"
             className="relative overflow-hidden rounded-md border border-neutral-200 bg-neutral-50"
             style={zoom === "fit"
               ? { width: `${fitSize.w}px`, height: `${fitSize.h}px`, flexShrink: 0, alignSelf: "center", touchAction: "none", userSelect: "none", margin: "auto" }
               : { width: `${(imgDims?.naturalWidth || 480) * zoom}px`,
                   height: `${(imgDims?.naturalHeight || 640) * zoom}px`,
                   flexShrink: 0, alignSelf: "flex-start", touchAction: "none", userSelect: "none" }}>
          <img ref={imgRef} src={rawUrl} alt="raw screenshot"
               onLoad={onImgLoad}
               draggable={false}
               loading="eager"
               decoding="async"
               className="pointer-events-none block select-none"
               style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain" }} />
        {imgLoaded && (
          <div data-testid="manual-crop-box" style={boxStyle}
               onMouseDown={startDrag("move")}
               onTouchStart={startDrag("move")}>
            <div style={h({ left: -9, top: -9, cursor: "nwse-resize" })}
                 onMouseDown={startDrag("tl")} onTouchStart={startDrag("tl")}
                 data-testid="manual-crop-handle-tl" />
            <div style={h({ right: -9, top: -9, cursor: "nesw-resize" })}
                 onMouseDown={startDrag("tr")} onTouchStart={startDrag("tr")}
                 data-testid="manual-crop-handle-tr" />
            <div style={h({ left: -9, bottom: -9, cursor: "nesw-resize" })}
                 onMouseDown={startDrag("bl")} onTouchStart={startDrag("bl")}
                 data-testid="manual-crop-handle-bl" />
            <div style={h({ right: -9, bottom: -9, cursor: "nwse-resize" })}
                 onMouseDown={startDrag("br")} onTouchStart={startDrag("br")}
                 data-testid="manual-crop-handle-br" />
            <div style={h({ left: "50%", top: -9, marginLeft: -9, cursor: "ns-resize" })}
                 onMouseDown={startDrag("t")} onTouchStart={startDrag("t")} />
            <div style={h({ left: "50%", bottom: -9, marginLeft: -9, cursor: "ns-resize" })}
                 onMouseDown={startDrag("b")} onTouchStart={startDrag("b")} />
            <div style={h({ left: -9, top: "50%", marginTop: -9, cursor: "ew-resize" })}
                 onMouseDown={startDrag("l")} onTouchStart={startDrag("l")} />
            <div style={h({ right: -9, top: "50%", marginTop: -9, cursor: "ew-resize" })}
                 onMouseDown={startDrag("r")} onTouchStart={startDrag("r")} />
          </div>
        )}
        </div>
      </div>

      {/* Actions · W-B.R2 · Kili primary + reset secondary.
          W-B.R10 · shrink-0 so the sticky-bottom bar in review-work-area
          keeps every action visible without scrolling.
          W-B.R16 · hidden when embedded in PosterEditor · the editor
          top bar owns the primary save action. */}
      {!embedded && (
      <div className="mx-auto flex w-full max-w-2xl shrink-0 flex-col gap-2">
        <div className="flex items-stretch gap-2">
          <button type="button" data-testid="manual-crop-save" onClick={save} disabled={saving}
                  className="flex-1 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-3 text-sm font-bold uppercase tracking-wider disabled:opacity-60"
                  style={{ background: KILI, color: INK, border: `2px solid ${INK}` }}>
            <span className="whitespace-nowrap">{saving ? "saving…" : "save crop"}</span>
            {!saving && (
              <span data-testid="manual-crop-save-shortcut"
                    aria-hidden
                    className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-sm px-1 text-[10px] font-bold"
                    style={{ background: INK, color: KILI, lineHeight: 1 }}>s</span>
            )}
          </button>
          <button type="button" data-testid="manual-crop-reset" onClick={reset}
                  className="whitespace-nowrap rounded-md border border-neutral-400 bg-white px-4 py-3 text-xs font-semibold text-neutral-700 hover:bg-neutral-50">
            reset the box
          </button>
        </div>
        <div className="flex items-center justify-between text-[11px] text-neutral-500">
          <span>drag the yellow box or any corner · raw never leaves admin</span>
          <button type="button" data-testid="manual-crop-no-poster"
                  onClick={() => onNoPoster?.()}
                  className="font-semibold text-neutral-700 underline decoration-dotted hover:text-neutral-900">
            no poster in this screenshot
          </button>
        </div>
        {onCancel && (
          <button type="button" data-testid="manual-crop-cancel" onClick={onCancel}
                  className="self-start text-[11px] text-neutral-500 hover:underline">
            close
          </button>
        )}
      </div>
      )}
    </div>
  );
});
